// src/store/thunks/poc/helia/announceFileToHapThunk.ts
// Thunk for announcing files to HAP via HTTP API (reliable fallback to pubsub)

import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import type { FileMetadata } from '@/types/poc/helia';
import { getHapApiUrl, isValidHapApiUrl } from '@/utils/hap-url.utils';
import { HapApiServiceImpl } from '@/services/hap/HapApiServiceImpl';

interface AnnounceFileToHapParams {
  sixId: string;
  cid: string;
  metadata: FileMetadata;
  bootstrapNode: string;
  swarmKey: string;
}

interface AnnounceFileToHapResult {
  success: boolean;
  cid: string;
  message: string;
  method: 'http';
}

/**
 * Announce a file to the HAP via HTTP API.
 * This provides a reliable alternative to pubsub for file announcements.
 *
 * Call this after adding a file to Helia to ensure the HAP receives the announcement.
 * Unlike pubsub which is unreliable, HTTP provides guaranteed delivery (or error).
 */
export const announceFileToHap = createAppAsyncThunk(
  'helia/announceFileToHap',
  async(
    { sixId, cid, metadata, bootstrapNode, swarmKey }: AnnounceFileToHapParams,
    { extra: { loggerService, pocServices } }
  ): Promise<AnnounceFileToHapResult> => {
    const log = loggerService.createLogger({ namespace: 'six3ro:hap-announce' });

    // Construct HAP API URL from bootstrap node
    const hapApiUrl = getHapApiUrl(bootstrapNode);

    if (!isValidHapApiUrl(hapApiUrl)) {
      log.warn('Unable to construct HAP API URL from bootstrap node', {
        bootstrapNode,
        cid
      });
      return {
        success: false,
        cid,
        message: 'Unable to determine HAP API URL',
        method: 'http'
      };
    }

    // Get local peer ID for the sender field
    const libp2pNode = pocServices.p2pStackManagerService.getLibp2pNode(sixId);
    const senderPeerId = libp2pNode?.peerId.toString();

    // Create HAP API service
    const hapApiService = new HapApiServiceImpl(loggerService);

    try {
      log.info('Announcing file to HAP via HTTP', {
        cid,
        fileName: metadata.name,
        sixId,
        hapApiUrl
      });

      const response = await hapApiService.announceFile(
        { hapApiUrl, swarmKey },
        {
          cid,
          metadata,
          sixId,
          senderPeerId
        }
      );

      if (response.accepted) {
        log.info('HAP accepted file announcement', {
          cid,
          message: response.message
        });

        return {
          success: true,
          cid,
          message: response.message,
          method: 'http'
        };
      } else {
        log.warn('HAP rejected file announcement', {
          cid,
          message: response.message
        });

        return {
          success: false,
          cid,
          message: response.message,
          method: 'http'
        };
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      log.error('Failed to announce file to HAP via HTTP', {
        cid,
        sixId,
        hapApiUrl,
        error: errorMessage
      });

      // Don't reject - return failure result so caller can handle gracefully
      return {
        success: false,
        cid,
        message: `HTTP announcement failed: ${errorMessage}`,
        method: 'http'
      };
    }
  }
);
